'use client'

import Link from 'next/link'
import { useRef, useState, useTransition } from 'react'
import { Input, Label, TextArea, TextField, toast } from '@heroui/react'

import { TurnstileWidget } from '@/components/forms/turnstile-widget'
import { LeadSuccess } from '@/components/leads/lead-success'
import { ContactMethodPicker } from '@/components/leads/contact-method-picker'
import { Button } from '@/components/ui/button'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { CONTACT_PATHS } from '@/config/contact'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import { pushFormSubmit } from '@/lib/analytics/funnel-events'
import { submitContactInquiry } from '@/lib/services/actions/inquiry'
import type { InquiryContactMethod } from '@/lib/validation/input/inquiry-schema'

interface ContactFormProps {
  locationId: string
  defaultName?: string
  defaultEmail?: string
}

const TOPICS = [
  { key: 'general', label: 'General question' },
  { key: 'booking', label: 'An existing booking or order' },
  { key: 'quote', label: 'Pricing or a quote' },
  { key: 'warranty', label: 'Warranty or a repair' },
  { key: 'commercial', label: 'Commercial or fleet work' },
  { key: 'other', label: 'Something else' },
] as const
type TopicKey = (typeof TOPICS)[number]['key']

/**
 * The general "send a message" form on /contact. Topics that have a better
 * home (quote, warranty, fleet) show an inline pointer to it but still accept
 * the message: a customer who wants to type should be able to.
 */
export function ContactForm({ locationId, defaultName = '', defaultEmail = '' }: ContactFormProps) {
  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState(false)
  const inFlightRef = useRef(false)

  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [phone, setPhone] = useState('')
  const [contactMethod, setContactMethod] = useState<InquiryContactMethod>('email')
  const [topic, setTopic] = useState<TopicKey>('general')
  const { instantQuoteEnabled } = useFeatureFlags()
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Bumped after a rejected submit: the token is single-use, so the widget has
  // to draw a fresh one before the customer can try again.
  const [turnstileNonce, setTurnstileNonce] = useState(0)

  const topicLabel = TOPICS.find((t) => t.key === topic)?.label ?? 'General question'
  const needsPhone = contactMethod !== 'email'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inFlightRef.current) return
    if (needsPhone && !phone.trim()) {
      toast.danger('Phone number needed', { description: 'Add a number so we can text or call you.' })
      return
    }
    inFlightRef.current = true
    startTransition(async () => {
      try {
        const result = await submitContactInquiry({
          inquiryType: 'GENERAL',
          source: 'contact-page',
          name,
          email,
          phone: phone || undefined,
          contactMethod,
          serviceInterest: topicLabel,
          message,
          shopLocationId: locationId,
          website,
          turnstileToken: turnstileToken ?? undefined,
        })
        if (result.success) {
          if (result.data?.id && result.data.id !== 'ignored')
            pushFormSubmit('contact', result.data.id, { email, phone })
          toast.success('Message sent', { description: 'We reply within one business day.' })
          setSubmitted(true)
          setPhone('')
          setMessage('')
          setTopic('general')
        } else {
          setTurnstileNonce((n) => n + 1)
          toast.danger('Something went wrong', { description: result.error || 'Please try again.' })
        }
      } finally {
        inFlightRef.current = false
      }
    })
  }

  if (submitted) {
    return (
      <LeadSuccess title="Message received" onReset={() => setSubmitted(false)} resetLabel="Send another message">
        <p>Thanks. A person at the shop reads every message and replies within one business day.</p>
        <p>Time-sensitive? Call us during shop hours and mention you also sent a message.</p>
      </LeadSuccess>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-4" aria-label="Contact form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField value={name} onChange={setName} isRequired>
            <Label>Your name</Label>
            <Input variant="outline" autoComplete="name" />
          </TextField>
          <TextField value={email} onChange={setEmail} isRequired>
            <Label>Email</Label>
            <Input type="email" variant="outline" autoComplete="email" />
          </TextField>
        </div>

        <div className="space-y-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <TextField value={phone} onChange={setPhone} isRequired={needsPhone} className="flex-1">
              <Label>Phone{needsPhone ? '' : ' (optional)'}</Label>
              <Input type="tel" inputMode="tel" variant="outline" autoComplete="tel" />
            </TextField>
            <ContactMethodPicker value={contactMethod} onChange={setContactMethod} />
          </div>
          <p className="text-xs text-muted">How would you like us to reply?</p>
        </div>

        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-foreground">What is this about?</span>
          <Select
            aria-label="Topic"
            selectedKeys={[topic]}
            onSelectionChange={(keys) => {
              const val = Array.from(keys as Iterable<unknown>)[0]
              if (typeof val === 'string') setTopic(val as TopicKey)
            }}
            variant="outline"
          >
            {TOPICS.map((t) => (
              <ListBoxItem key={t.key} id={t.key}>
                {t.label}
              </ListBoxItem>
            ))}
          </Select>
          {topic === 'quote' && (
            <p className="text-xs text-muted">
              Want a number back?{' '}
              <Link href={CONTACT_PATHS.quote} className="font-semibold text-foreground underline">
                The quote form
              </Link>{' '}
              asks for the vehicle details we need
              {instantQuoteEnabled && (
                <>
                  , or try the{' '}
                  <Link href={CONTACT_PATHS.instantQuote} className="font-semibold text-foreground underline">
                    instant estimate
                  </Link>
                </>
              )}
              .
            </p>
          )}
          {topic === 'warranty' && (
            <p className="text-xs text-muted">
              For a wrap we installed, the{' '}
              <Link href={CONTACT_PATHS.warrantyClaim} className="font-semibold text-foreground underline">
                warranty claim form
              </Link>{' '}
              lets you attach photos and starts the 14-day clock on record.
            </p>
          )}
          {topic === 'commercial' && (
            <p className="text-xs text-muted">
              Two or more vehicles? The{' '}
              <Link href={CONTACT_PATHS.fleetQuote} className="font-semibold text-foreground underline">
                fleet quote form
              </Link>{' '}
              goes straight to the fleet team.
            </p>
          )}
        </div>

        <TextField value={message} onChange={setMessage} isRequired>
          <Label>Message</Label>
          <TextArea variant="outline" rows={5} placeholder="What can we help with?" />
        </TextField>

        {/* Honeypot: hidden from people, filled by bots. */}
        <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
          <label>
            Website
            <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </div>

        <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileNonce} />

        <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isPending} isDisabled={isPending}>
          {isPending ? 'Sending...' : 'Send message'}
        </Button>
      </form>
    </div>
  )
}
